import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Divider,
  Avatar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  Alert,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonIcon from "@mui/icons-material/Person";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import KeyIcon from "@mui/icons-material/Key";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { makeStyles } from "@mui/styles";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import Spinner from "../../components/Spinner";
import { setLoading, showToast } from "../../store/slices/common";
import { banUser, unbanUser } from "../../http/banUser";
import {
  adjustUserVisibleRank,
  createTempPassword,
  previewUserModification,
  revertMatchLp,
  setStaffStatus,
  undoRevertMatchLp,
} from "../../http/userModification";
import ConfirmDialog from "../CuratedMatches/ConfirmDialog";
import UserPostsTab from "./UserPostsTab";
import UserPagentHistoryTab from "./UserPagentHistoryTab";

const useStyles = makeStyles(() => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  card: {
    padding: "20px",
    borderRadius: "6px",
    boxShadow:
      "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
    maxWidth: "100%",
    width: "100%",
    boxSizing: "border-box",
  },
  searchRow: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    flexWrap: "wrap",
    width: "100%",
  },
  primaryButton: {
    backgroundColor: "#142C5C !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    height: "40px !important",
    textTransform: "none !important",
    "&:hover": {
      backgroundColor: "#0f2247 !important",
    },
  },
  banButton: {
    backgroundColor: "#c62828 !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    height: "40px !important",
    textTransform: "none !important",
    "&:hover": {
      backgroundColor: "#b71c1c !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "#ef9a9a !important",
      color: "#fff !important",
    },
  },
  unbanButton: {
    backgroundColor: "#2e7d32 !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    height: "40px !important",
    textTransform: "none !important",
    "&:hover": {
      backgroundColor: "#1b5e20 !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "#a5d6a7 !important",
      color: "#fff !important",
    },
  },
  staffButton: {
    backgroundColor: "#DC2626 !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    height: "40px !important",
    textTransform: "none !important",
    "&:hover": {
      backgroundColor: "#B91C1C !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "#FCA5A5 !important",
      color: "#fff !important",
    },
  },
  metaRow: {
    display: "grid",
    gridTemplateColumns: "minmax(100px, 140px) 1fr",
    gap: "4px 12px",
    marginBottom: "4px",
    wordBreak: "break-word",
    "@media (max-width: 600px)": {
      gridTemplateColumns: "1fr",
    },
  },
  metaLabel: {
    color: "#777",
    fontWeight: 600,
    fontSize: "13px",
  },
  metaValue: {
    color: "#333",
    fontSize: "13px",
    wordBreak: "break-word",
  },
  cascadeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: "10px",
    marginTop: "12px",
  },
  cascadeItem: {
    background: "#f5f5f5",
    borderRadius: "6px",
    padding: "10px 12px",
  },
}));

const BAN_CASCADE_LABELS = [
  { key: "inProgressMatches", label: "Matches to forfeit" },
  { key: "totalMatches", label: "Matches to hide" },
  { key: "posts", label: "Posts" },
  { key: "likes", label: "Likes" },
  { key: "comments", label: "Comments" },
  { key: "follows", label: "Follow edges" },
  { key: "votes", label: "Votes" },
];

const UNBAN_CASCADE_LABELS = [
  { key: "totalMatches", label: "Matches to restore" },
  { key: "posts", label: "Posts to restore" },
  { key: "likes", label: "Likes to restore" },
  { key: "comments", label: "Comments to restore" },
  { key: "follows", label: "Follow edges to restore" },
  { key: "votes", label: "Votes to restore" },
];

const DIVISION_LABELS = { 3: "III", 2: "II", 1: "I" };
// Ladder order, lowest first. UNRANKED (placements) sits before the ladder.
const RANK_TIERS = [
  "UNRANKED",
  "IRON",
  "BRONZE",
  "SILVER",
  "GOLD",
  "PLATINUM",
  "DIAMOND",
  "LEGEND",
  "GRANDLEGEND",
  "IDOLIZED",
];

const STAFF_RED = "#DC2626";

function formatPublicRank(rank) {
  if (!rank?.publicRankTier) return "—";
  const tier = String(rank.publicRankTier);
  if (
    tier === "UNRANKED" ||
    tier === "LEGEND" ||
    tier === "GRANDLEGEND" ||
    tier === "IDOLIZED" ||
    tier === "STAFF"
  ) {
    const name =
      tier === "GRANDLEGEND" ? "Grand Legend" : tier === "IDOLIZED" ? "Idolized" : tier;
    const stars =
      tier !== "UNRANKED" && tier !== "STAFF" && rank.publicRankLP != null
        ? ` · ${rank.publicRankLP}★`
        : "";
    return `${name}${stars}`;
  }
  const division = DIVISION_LABELS[rank.publicRankDivision] || "";
  const lp = rank.publicRankLP != null ? ` · ${rank.publicRankLP} LP` : "";
  return `${tier}${division ? ` ${division}` : ""}${lp}`;
}

function UserProfileHeader({ user, isBanned }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        mb: 2,
      }}
    >
      <Avatar
        src={user.profilePicture || undefined}
        alt={user.username}
        sx={{
          width: 72,
          height: 72,
          border: "2px solid #142C5C",
          bgcolor: "#e0e0e0",
        }}
      >
        {!user.profilePicture && (
          <PersonIcon sx={{ fontSize: 36, color: "#777" }} />
        )}
      </Avatar>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 700, color: "#142C5C", fontSize: "18px" }}>
            @{user.username}
            {isBanned ? " (banned)" : ""}
          </Typography>
          {user.rank?.isStaff && (
            <Chip
              size="small"
              icon={<StarIcon sx={{ color: "#fff !important" }} />}
              label="Staff"
              sx={{
                backgroundColor: STAFF_RED,
                color: "#fff",
                fontWeight: 700,
                fontSize: "11px",
              }}
            />
          )}
        </Box>
        <Typography
          sx={{
            fontSize: "14px",
            color: user.rank?.isStaff ? STAFF_RED : "#333",
            fontWeight: user.rank?.isStaff ? 700 : 400,
            mt: 0.5,
          }}
        >
          {formatPublicRank(user.rank)}
        </Typography>
        {user.rank && (
          <Typography sx={{ fontSize: "12px", color: "#777", mt: 0.25 }}>
            {user.rank.matchCount ?? 0} matches
            {user.rank.points != null ? ` · hidden MMR ${user.rank.points}` : ""}
            {` · hidden for fun MMR ${user.rank.forFunPoints ?? 1200}`}
          </Typography>
        )}
        <Typography sx={{ fontSize: "12px", color: "#777", mt: 0.25 }}>
          {user.followersCount ?? 0} followers · {user.followingCount ?? 0} following
        </Typography>
      </Box>
    </Box>
  );
}

const UserModification = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Deep link from Anti-Cheat / other pages: /user-modification?username=foo
  // prefills the lookup and runs it once.
  const initialUsername = (searchParams.get("username") || "").trim().replace(/^@/, "");
  const [usernameInput, setUsernameInput] = useState(initialUsername);
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [confirmText, setConfirmText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [tempPasswordResult, setTempPasswordResult] = useState(null);

  const [lpDelta, setLpDelta] = useState("");
  const [rankTier, setRankTier] = useState("IRON");
  const [rankDivision, setRankDivision] = useState(3);
  const [rankLp, setRankLp] = useState(0);
  const [lpAdjustMode, setLpAdjustMode] = useState("delta");
  const [banDuration, setBanDuration] = useState("permanent");
  const [banReason, setBanReason] = useState("");

  const targetUsername = preview?.user?.username || "";
  const isBanned = Boolean(preview?.user?.isBanned);
  const isTimedOnlyBan = Boolean(
    isBanned &&
      preview?.user &&
      !preview.user.isDeleted &&
      !preview.user.isAccountBlocked &&
      preview.user.bannedUntil,
  );
  const isStaff = Boolean(preview?.user?.rank?.isStaff);
  const canConfirmBan =
    Boolean(targetUsername) &&
    confirmText.trim().replace(/^@/, "").toLowerCase() ===
      targetUsername.toLowerCase() &&
    (isBanned || banReason.trim().length > 0);

  const extractErrorMessage = (err) =>
    err?.response?.data?.message ||
    err?.response?.data?.data?.message ||
    err?.message ||
    "Request failed";

  const handleLookup = async () => {
    const username = usernameInput.trim().replace(/^@/, "");
    if (!username) {
      dispatch(
        showToast({ color: "error", msg: "Enter a username to look up" }),
      );
      return;
    }

    setBusy(true);
    dispatch(setLoading(true));
    setActionResult(null);
    setTempPasswordResult(null);
    setConfirmText("");
    setPreview(null);

    try {
      const response = await previewUserModification(username);
      const payload = response?.data || response;
      setPreview(payload);
      setBanReason("");
      setBanDuration("permanent");
      if (payload?.user?.rank) {
        setRankTier(payload.user.rank.publicRankTier || "IRON");
        setRankDivision(payload.user.rank.publicRankDivision ?? 3);
        setRankLp(payload.user.rank.publicRankLP ?? 0);
      }
    } catch (err) {
      dispatch(showToast({ color: "error", msg: extractErrorMessage(err) }));
    } finally {
      setBusy(false);
      dispatch(setLoading(false));
    }
  };

  // Run the lookup once when the page opened with ?username=. handleLookup
  // reads usernameInput, which was seeded from the same value above.
  const autoLookupRef = useRef(false);
  useEffect(() => {
    if (initialUsername && !autoLookupRef.current) {
      autoLookupRef.current = true;
      handleLookup();
    }
  }, [initialUsername]); // eslint-disable-line

  const refreshPreview = async () => {
    if (!targetUsername) return;
    const response = await previewUserModification(targetUsername);
    const payload = response?.data || response;
    setPreview(payload);
  };

  const handleBanConfirm = async () => {
    setConfirmOpen(false);
    if (!canConfirmBan || !targetUsername) return;

    setBusy(true);
    dispatch(setLoading(true));

    try {
      const response = isBanned
        ? await unbanUser(targetUsername)
        : await banUser(targetUsername, {
            durationDays:
              banDuration === "permanent" ? undefined : Number(banDuration),
            reason: banReason.trim(),
          });
      const payload = response?.data || response;
      setActionResult({ ...payload, action: isBanned ? "unban" : "ban" });
      setPreview(null);
      setConfirmText("");
      setBanReason("");
      setBanDuration("permanent");
      dispatch(
        showToast({
          color: "success",
          msg: isBanned
            ? `@${payload?.user?.username || targetUsername} has been unbanned`
            : payload?.user?.keptExistingBan
              ? `@${payload?.user?.username || targetUsername} already has a longer ban, so it was left in place`
              : `@${payload?.user?.username || targetUsername} has been banned`,
        }),
      );
    } catch (err) {
      dispatch(showToast({ color: "error", msg: extractErrorMessage(err) }));
    } finally {
      setBusy(false);
      dispatch(setLoading(false));
    }
  };

  const handleAdjustLp = async () => {
    if (!targetUsername) return;

    setBusy(true);
    dispatch(setLoading(true));

    try {
      const payload =
        lpAdjustMode === "delta"
          ? { username: targetUsername, lpDelta: Number(lpDelta) }
          : {
              username: targetUsername,
              publicRankTier: rankTier,
              publicRankDivision: Number(rankDivision),
              publicRankLP: Number(rankLp),
            };

      const response = await adjustUserVisibleRank(payload);
      const data = response?.data || response;
      setPreview((prev) => ({
        ...prev,
        user: data.user,
      }));
      dispatch(
        showToast({
          color: "success",
          msg: `Updated visible rank for @${targetUsername}`,
        }),
      );
    } catch (err) {
      dispatch(showToast({ color: "error", msg: extractErrorMessage(err) }));
    } finally {
      setBusy(false);
      dispatch(setLoading(false));
    }
  };

  const handleTempPasswordConfirm = async () => {
    setConfirmOpen(false);
    setConfirmKind(null);
    if (!targetUsername) return;

    setBusy(true);
    dispatch(setLoading(true));

    try {
      const response = await createTempPassword(targetUsername);
      const data = response?.data || response;
      setTempPasswordResult(data);
      dispatch(
        showToast({
          color: "success",
          msg: `Temporary password created for @${targetUsername}`,
        }),
      );
    } catch (err) {
      dispatch(showToast({ color: "error", msg: extractErrorMessage(err) }));
    } finally {
      setBusy(false);
      dispatch(setLoading(false));
    }
  };

  const handleStaffStatusConfirm = async () => {
    setConfirmOpen(false);
    setConfirmKind(null);
    if (!targetUsername) return;

    const nextIsStaff = !isStaff;
    setBusy(true);
    dispatch(setLoading(true));

    try {
      const response = await setStaffStatus({
        username: targetUsername,
        isStaff: nextIsStaff,
      });
      const data = response?.data || response;
      setPreview((prev) => ({
        ...prev,
        user: data.user,
      }));
      if (data.user?.rank) {
        setRankTier(data.user.rank.publicRankTier || "IRON");
        setRankDivision(data.user.rank.publicRankDivision ?? 3);
        setRankLp(data.user.rank.publicRankLP ?? 0);
      }
      dispatch(
        showToast({
          color: "success",
          msg: nextIsStaff
            ? `@${targetUsername} is now Staff (red star)`
            : `Staff status removed from @${targetUsername}${
                data.staff?.after?.publicRankTier
                  ? ` — restored to ${formatPublicRank(data.staff.after)}`
                  : ""
              }`,
        }),
      );
    } catch (err) {
      dispatch(showToast({ color: "error", msg: extractErrorMessage(err) }));
    } finally {
      setBusy(false);
      dispatch(setLoading(false));
    }
  };

  const handleCopyTempPassword = async () => {
    try {
      await navigator.clipboard.writeText(
        tempPasswordResult?.temporaryPassword || "",
      );
      dispatch(showToast({ color: "success", msg: "Password copied" }));
    } catch {
      dispatch(
        showToast({
          color: "error",
          msg: "Copy failed — select and copy manually",
        }),
      );
    }
  };

  const openRevertConfirm = (match, mode) => {
    setConfirmKind({ match, mode });
    setConfirmOpen(true);
  };

  const handleRevertConfirm = async () => {
    const kind = confirmKind;
    setConfirmOpen(false);
    setConfirmKind(null);

    if (!kind?.match || !targetUsername) return;

    setBusy(true);
    dispatch(setLoading(true));

    try {
      const response = await revertMatchLp({
        username: targetUsername,
        matchId: kind.match._id,
        mode: kind.mode,
      });
      const data = response?.data || response;
      setPreview((prev) => ({
        ...prev,
        user: data.user,
        matches: data.matches,
      }));
      dispatch(
        showToast({
          color: "success",
          msg: "Match visible LP reverted (best-effort estimate)",
        }),
      );
    } catch (err) {
      dispatch(showToast({ color: "error", msg: extractErrorMessage(err) }));
    } finally {
      setBusy(false);
      dispatch(setLoading(false));
    }
  };

  const handleUndoRevertConfirm = async () => {
    const kind = confirmKind;
    setConfirmOpen(false);
    setConfirmKind(null);

    if (!kind?.match || !targetUsername) return;

    setBusy(true);
    dispatch(setLoading(true));

    try {
      const response = await undoRevertMatchLp({
        username: targetUsername,
        matchId: kind.match._id,
      });
      const data = response?.data || response;
      setPreview((prev) => ({
        ...prev,
        user: data.user,
        matches: data.matches,
      }));
      dispatch(
        showToast({
          color: "success",
          msg: "Revert undone for this match",
        }),
      );
    } catch (err) {
      dispatch(showToast({ color: "error", msg: extractErrorMessage(err) }));
    } finally {
      setBusy(false);
      dispatch(setLoading(false));
    }
  };

  const cascadeLabels = isBanned ? UNBAN_CASCADE_LABELS : BAN_CASCADE_LABELS;

  const previousRankLabel = preview?.user?.rank?.staffPreviousRank
    ? formatPublicRank({
        publicRankTier: preview.user.rank.staffPreviousRank.tier,
        publicRankDivision: preview.user.rank.staffPreviousRank.division,
        publicRankLP: preview.user.rank.staffPreviousRank.lp,
      })
    : null;

  const confirmDialogProps =
    confirmKind?.type === "staff-status"
      ? {
          title: isStaff ? "Remove staff status" : "Grant staff status",
          message: isStaff
            ? `Remove Staff from @${targetUsername}? Their red star goes away and their visible rank is restored to ${
                previousRankLabel || "the rank derived from their hidden MMR"
              }. Hidden MMR and match count stay unchanged.`
            : `Make @${targetUsername} Staff? They get the red Staff star and the same privileges as a high-rank player (livestream, home-feed posts, groups, etc.), and stop gaining or losing LP. Their current rank (${formatPublicRank(
                preview?.user?.rank,
              )}) is saved and restored if you remove Staff later. Hidden MMR and match count stay unchanged.`,
          onConfirm: handleStaffStatusConfirm,
        }
      : confirmKind?.type === "temp-password"
      ? {
          title: "Generate temporary password",
          message: `Replace @${targetUsername}'s password with a new temporary one? Their current password stops working and they will be logged out of all devices.${
            preview?.user?.email
              ? ` The new password will also be emailed to ${preview.user.email}.`
              : " No email on file — you'll need to deliver it to them yourself."
          }`,
          onConfirm: handleTempPasswordConfirm,
        }
      : confirmKind?.match
      ? {
          title:
            confirmKind.mode === "undo_revert"
              ? "Undo previous revert"
              : confirmKind.mode === "revert_loss"
                ? "Revert loss (visible LP)"
                : "Revert win (visible LP)",
          message:
            confirmKind.mode === "undo_revert"
              ? "Undo the previous revert for this match? This will re-apply the same visible LP deltas that were reverted. Hidden MMR and match count stay unchanged."
              : confirmKind.mode === "revert_loss"
                ? `Credit visible LP back to @${targetUsername} for this loss? The opponent's LP will not change. Hidden MMR and match count stay unchanged. LP amount is a best-effort estimate.`
                : `Undo visible LP for both players in this match? @${targetUsername} loses LP gained; opponent gets LP back. Hidden MMR and match count stay unchanged. LP amounts are best-effort estimates.`,
          onConfirm:
            confirmKind.mode === "undo_revert"
              ? handleUndoRevertConfirm
              : handleRevertConfirm,
        }
      : {
          title: isBanned
            ? isTimedOnlyBan
              ? "Confirm lift suspension"
              : "Confirm unban"
            : banDuration === "permanent"
              ? "Confirm permanent ban"
              : `Confirm ${banDuration}-day ban`,
          message: isBanned
            ? isTimedOnlyBan
              ? `Lift the suspension for @${targetUsername}? They will be able to use the app again. This suspension did not delete their content.`
              : `Unban @${targetUsername}? They will be able to log in again and soft-deleted content will be restored. Forfeited match outcomes from the ban are not reversed.`
            : banDuration === "permanent"
              ? `Permanently ban @${targetUsername}? This blocks login, forfeits in-progress matches, and soft-deletes their account and related content. They will see this reason: "${banReason.trim()}". You can unban later to restore access.`
              : `Suspend @${targetUsername} for ${banDuration} days? The app stays locked until then, and they will see this reason: "${banReason.trim()}". Their account and content are not deleted.`,
          onConfirm: handleBanConfirm,
        };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
      {busy && <Spinner />}
      <Box className={classes.header}>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: "15px", sm: "16px", md: "18px", lg: "20px" },
            marginTop: "4px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ManageAccountsIcon sx={{ color: "#142C5C" }} />
          Account Tools
        </Typography>
      </Box>

      <Paper className={classes.card} elevation={0}>
        <Typography sx={{ fontWeight: 600, mb: 1, color: "#142C5C" }}>
          Look up by username
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "#666", mb: 2 }}>
          Search a user to ban/unban, grant or remove Staff status (red
          star), issue a temporary password, adjust visible rank (LP), revert
          match LP, review their pagent history, or review their posts. Hidden
          MMR is never changed here. More tools may be added later.
        </Typography>

        <Box className={classes.searchRow}>
          <TextField
            size="small"
            label="Username"
            placeholder="username"
            value={usernameInput}
            onChange={(e) => setUsernameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleLookup();
            }}
            sx={{ minWidth: 260, flex: "1 1 260px", maxWidth: "100%" }}
          />
          <Button
            className={classes.primaryButton}
            variant="contained"
            onClick={handleLookup}
            disabled={busy}
          >
            Look up
          </Button>
        </Box>

        {actionResult?.user && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: "6px",
              backgroundColor:
                actionResult.action === "unban" ? "#e3f2fd" : "#e8f5e9",
              border:
                actionResult.action === "unban"
                  ? "1px solid #90caf9"
                  : "1px solid #a5d6a7",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: actionResult.action === "unban" ? "#1565c0" : "#2e7d32",
              }}
            >
              {actionResult.action === "unban" ? "Unbanned" : "Banned"} @
              {actionResult.user.username}
            </Typography>
          </Box>
        )}

        {preview?.user && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <UserProfileHeader user={preview.user} isBanned={isBanned} />

            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider", maxWidth: "100%" }}
            >
              <Tab label="Account" sx={{ textTransform: "none" }} />
              <Tab label="Visible rank / LP" sx={{ textTransform: "none" }} />
              <Tab label="Matches" sx={{ textTransform: "none" }} />
              <Tab label="Pagent history" sx={{ textTransform: "none" }} />
              <Tab
                label={`Posts${
                  preview.cascade?.posts != null
                    ? ` (${preview.cascade.posts})`
                    : ""
                }`}
                sx={{ textTransform: "none" }}
              />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                <Box className={classes.metaRow}>
                  <span className={classes.metaLabel}>User ID</span>
                  <span className={classes.metaValue}>{preview.user._id}</span>
                  <span className={classes.metaLabel}>Email</span>
                  <span className={classes.metaValue}>
                    {preview.user.email || "—"}
                  </span>
                  <span className={classes.metaLabel}>Followers</span>
                  <span className={classes.metaValue}>
                    {preview.user.followersCount ?? 0}
                  </span>
                  <span className={classes.metaLabel}>Following</span>
                  <span className={classes.metaValue}>
                    {preview.user.followingCount ?? 0}
                  </span>
                  <span className={classes.metaLabel}>Blocked</span>
                  <span className={classes.metaValue}>
                    {preview.user.isAccountBlocked ? "Yes" : "No"}
                  </span>
                  <span className={classes.metaLabel}>Deleted</span>
                  <span className={classes.metaValue}>
                    {preview.user.isDeleted ? "Yes" : "No"}
                  </span>
                  <span className={classes.metaLabel}>Staff</span>
                  <span
                    className={classes.metaValue}
                    style={isStaff ? { color: STAFF_RED, fontWeight: 700 } : undefined}
                  >
                    {isStaff
                      ? `Yes${
                          preview.user.rank?.staffGrantedAt
                            ? ` · since ${dayjs(preview.user.rank.staffGrantedAt).format(
                                "MM/DD/YYYY",
                              )}`
                            : ""
                        }`
                      : "No"}
                  </span>
                </Box>

                {!isBanned && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "14px" }}>
                      Staff status
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "#666", mb: 1.5 }}>
                      Staff accounts show a red <strong>Staff</strong> star in place
                      of a competitive rank and get the same privileges as a
                      high-rank player (livestream, home-feed posts, groups,
                      rap rank gates, etc.). They never gain or lose LP. Hidden
                      MMR is untouched so matchmaking still works.{" "}
                      {isStaff
                        ? `Removing it restores ${
                            previousRankLabel || "the rank derived from hidden MMR"
                          }.`
                        : "Their current rank is saved and restored if you remove Staff later."}
                    </Typography>
                    <Button
                      className={isStaff ? classes.primaryButton : classes.staffButton}
                      variant="contained"
                      startIcon={isStaff ? <StarOutlineIcon /> : <StarIcon />}
                      disabled={busy}
                      onClick={() => {
                        setConfirmKind({ type: "staff-status" });
                        setConfirmOpen(true);
                      }}
                    >
                      {isStaff ? "Remove staff status" : "Grant staff status"}
                    </Button>
                  </Box>
                )}

                {!isBanned && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "14px" }}>
                      Temporary password
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "#666", mb: 1.5 }}>
                      Replaces the user&apos;s password with a random temporary
                      one and logs them out everywhere.{" "}
                      {preview.user.email
                        ? "It will also be emailed to them."
                        : "No email on file — share it with the user yourself."}
                    </Typography>
                    <Button
                      className={classes.primaryButton}
                      variant="contained"
                      startIcon={<KeyIcon />}
                      disabled={busy}
                      onClick={() => {
                        setConfirmKind({ type: "temp-password" });
                        setConfirmOpen(true);
                      }}
                    >
                      Generate temporary password
                    </Button>

                    {tempPasswordResult?.temporaryPassword && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: "6px",
                          backgroundColor: "#fff8e1",
                          border: "1px solid #ffe082",
                          maxWidth: 520,
                        }}
                      >
                        <Typography sx={{ fontSize: "13px", color: "#666" }}>
                          Temporary password for @
                          {tempPasswordResult.user?.username || targetUsername}{" "}
                          — shown once, copy it now:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "20px",
                              fontWeight: 700,
                              letterSpacing: "1px",
                            }}
                          >
                            {tempPasswordResult.temporaryPassword}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyTempPassword}
                            sx={{ textTransform: "none" }}
                          >
                            Copy
                          </Button>
                        </Box>
                        <Typography sx={{ fontSize: "12px", color: "#888", mt: 0.5 }}>
                          {tempPasswordResult.deliveredVia?.includes("email")
                            ? `Also emailed to ${
                                tempPasswordResult.user?.email || "the user"
                              }.`
                            : "Not emailed — deliver it to the user directly."}{" "}
                          All their sessions were logged out.
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ mt: 2 }} />
                  </Box>
                )}

                <Typography sx={{ fontWeight: 600, mt: 2, mb: 0.5, fontSize: "14px" }}>
                  {isTimedOnlyBan
                    ? "Current suspension"
                    : isBanned
                      ? "Restore preview"
                      : banDuration === "permanent"
                        ? "Ban cascade preview"
                        : "Timed suspension"}
                </Typography>
                {!isBanned && banDuration !== "permanent" && (
                  <Typography sx={{ fontSize: "13px", color: "#444", mb: 1 }}>
                    A {banDuration}-day suspension locks the app until it ends.
                    It does not forfeit matches or delete their content.
                  </Typography>
                )}
                {isTimedOnlyBan && (
                  <Typography sx={{ fontSize: "13px", color: "#444", mb: 1 }}>
                    Suspended until{" "}
                    {dayjs(preview.user.bannedUntil).format("MMM D, YYYY h:mm A")}
                    . Reason shown to them: {preview.user.banReason || "—"}. Lifting
                    this only clears the lock.
                  </Typography>
                )}
                {(isBanned ? !isTimedOnlyBan : banDuration === "permanent") && (
                  <Box className={classes.cascadeGrid}>
                    {cascadeLabels.map(({ key, label }) => (
                      <Box key={key} className={classes.cascadeItem}>
                        <Typography sx={{ fontSize: "12px", color: "#777" }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>
                          {preview.cascade?.[key] ?? 0}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {!isBanned && (
                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField
                      select
                      size="small"
                      label="Duration"
                      value={banDuration}
                      onChange={(e) => setBanDuration(e.target.value)}
                      sx={{ maxWidth: 280 }}
                    >
                      <MenuItem value="7">7 days</MenuItem>
                      <MenuItem value="14">14 days</MenuItem>
                      <MenuItem value="21">21 days</MenuItem>
                      <MenuItem value="permanent">Permanent</MenuItem>
                    </TextField>
                    <TextField
                      size="small"
                      label="Reason shown to the user"
                      placeholder="Why this account is banned"
                      value={banReason}
                      onChange={(e) => setBanReason(e.target.value.slice(0, 500))}
                      multiline
                      minRows={2}
                      helperText={`${banReason.trim().length}/500`}
                      fullWidth
                    />
                  </Box>
                )}

                <Box sx={{ mt: 3 }}>
                  <Typography sx={{ fontSize: "13px", color: "#666", mb: 1 }}>
                    Type <strong>@{targetUsername}</strong> to enable{" "}
                    {isBanned ? (isTimedOnlyBan ? "Lift suspension" : "Unban") : "Ban"}
                  </Typography>
                  <Box className={classes.searchRow}>
                    <TextField
                      size="small"
                      label="Confirm username"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      sx={{ minWidth: 260, flex: "1 1 260px", maxWidth: "100%" }}
                    />
                    {isBanned ? (
                      <Button
                        className={classes.unbanButton}
                        variant="contained"
                        startIcon={<LockOpenIcon />}
                        disabled={!canConfirmBan || busy}
                        onClick={() => {
                          setConfirmKind(null);
                          setConfirmOpen(true);
                        }}
                      >
                        {isTimedOnlyBan ? "Lift suspension" : "Unban user"}
                      </Button>
                    ) : (
                      <Button
                        className={classes.banButton}
                        variant="contained"
                        startIcon={<BlockIcon />}
                        disabled={!canConfirmBan || busy}
                        onClick={() => {
                          setConfirmKind(null);
                          setConfirmOpen(true);
                        }}
                      >
                        Ban user
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            {activeTab === 1 && isStaff && (
              <Alert severity="warning" sx={{ fontSize: "13px" }}>
                @{targetUsername} has <strong>Staff</strong> status, so their
                visible rank is frozen at STAFF. Remove staff status from the
                Account tab to edit their rank
                {previousRankLabel ? ` (saved rank: ${previousRankLabel})` : ""}.
              </Alert>
            )}

            {activeTab === 1 && !isStaff && (
              <Box>
                <Alert severity="info" sx={{ mb: 2, fontSize: "13px" }}>
                  Changes apply to visible rank only (tier, division, LP). Hidden
                  MMR ({preview.user.rank?.points ?? "—"}) is not modified.
                </Alert>

                <Tabs
                  value={lpAdjustMode}
                  onChange={(_, v) => setLpAdjustMode(v)}
                  sx={{ mb: 2 }}
                >
                  <Tab label="LP delta" value="delta" sx={{ textTransform: "none" }} />
                  <Tab label="Set rank" value="set" sx={{ textTransform: "none" }} />
                </Tabs>

                {lpAdjustMode === "delta" ? (
                  <Box className={classes.searchRow}>
                    <TextField
                      size="small"
                      type="number"
                      label="LP delta (+/-)"
                      value={lpDelta}
                      onChange={(e) => setLpDelta(e.target.value)}
                      helperText="Positive adds LP; negative removes. Promotions/demotions apply automatically."
                      sx={{ minWidth: 200 }}
                    />
                    <Button
                      className={classes.primaryButton}
                      variant="contained"
                      disabled={busy || lpDelta === ""}
                      onClick={handleAdjustLp}
                    >
                      Apply delta
                    </Button>
                  </Box>
                ) : (
                  <Box className={classes.searchRow}>
                    <TextField
                      select
                      size="small"
                      label="Tier"
                      value={rankTier}
                      onChange={(e) => setRankTier(e.target.value)}
                      sx={{ minWidth: 140 }}
                    >
                      {RANK_TIERS.map((tier) => (
                        <MenuItem key={tier} value={tier}>
                          {tier}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Division"
                      value={rankDivision}
                      onChange={(e) => setRankDivision(e.target.value)}
                      sx={{ minWidth: 100 }}
                    >
                      <MenuItem value={3}>III</MenuItem>
                      <MenuItem value={2}>II</MenuItem>
                      <MenuItem value={1}>I</MenuItem>
                    </TextField>
                    <TextField
                      size="small"
                      type="number"
                      label={
                        rankTier === "LEGEND" ||
                        rankTier === "GRANDLEGEND" ||
                        rankTier === "IDOLIZED"
                          ? "Stars"
                          : "LP (0–99)"
                      }
                      value={rankLp}
                      onChange={(e) => setRankLp(e.target.value)}
                      inputProps={{
                        min: 0,
                        max:
                          rankTier === "LEGEND" ||
                          rankTier === "GRANDLEGEND" ||
                          rankTier === "IDOLIZED"
                            ? 1000000
                            : 99,
                      }}
                      helperText={
                        rankTier === "LEGEND"
                          ? "500 or more promotes to Grand Legend and spends those stars."
                          : undefined
                      }
                      sx={{ minWidth: 160 }}
                    />
                    <Button
                      className={classes.primaryButton}
                      variant="contained"
                      disabled={busy}
                      onClick={handleAdjustLp}
                    >
                      Set rank
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Alert severity="warning" sx={{ mb: 2, fontSize: "13px" }}>
                  LP deltas are not stored on matches — revert uses a best-effort
                  recalculation. Contest matches are listed but cannot be
                  reverted. Group duet reverts apply to the group&apos;s visible
                  rank (shared by both members).
                </Alert>

                <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ended</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Opponent</TableCell>
                        <TableCell>Est. LP</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(preview.matches || []).map((match) => (
                        <TableRow key={match._id}>
                          <TableCell sx={{ fontSize: "12px" }}>
                            {match.matchEndTime
                              ? dayjs(match.matchEndTime).format("MM/DD HH:mm")
                              : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={match.matchType}
                              sx={{ fontSize: "11px" }}
                            />
                            {match.isGroupDuet && (
                              <Chip
                                size="small"
                                label="Duet"
                                sx={{ ml: 0.5, fontSize: "11px" }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {match.isContest
                              ? "—"
                              : match.viewedUserRole || "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px" }}>
                            {match.opponent?.username
                              ? `@${match.opponent.username}`
                              : "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px" }}>
                            {match.isContest
                              ? "N/A"
                              : match.estimatedLpChange != null
                                ? `${match.estimatedLpChange > 0 ? "+" : ""}${match.estimatedLpChange}`
                                : "—"}
                          </TableCell>
                          <TableCell>
                            {match.isContest ? (
                              <Typography sx={{ fontSize: "12px", color: "#888" }}>
                                Contest matches cannot be reverted
                              </Typography>
                            ) : match.adminLpRevertedAt && !match.adminLpRevertUndoneAt ? (
                              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                                <Chip
                                  size="small"
                                  color="default"
                                  label={`Reverted (${match.adminLpRevertMode})`}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={busy}
                                  onClick={() => openRevertConfirm(match, "undo_revert")}
                                  sx={{ textTransform: "none" }}
                                >
                                  Undo revert
                                </Button>
                              </Box>
                            ) : match.availableActions?.includes("revert_loss") ? (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={busy}
                                onClick={() =>
                                  openRevertConfirm(match, "revert_loss")
                                }
                                sx={{ textTransform: "none" }}
                              >
                                Revert loss
                              </Button>
                            ) : match.availableActions?.includes("revert_win") ? (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={busy}
                                onClick={() =>
                                  openRevertConfirm(match, "revert_win")
                                }
                                sx={{ textTransform: "none" }}
                              >
                                Revert win
                              </Button>
                            ) : (
                              <Typography sx={{ fontSize: "12px", color: "#888" }}>
                                {match.revertReason || "—"}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!preview.matches?.length && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No matches found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Button
                  size="small"
                  sx={{ mt: 1, textTransform: "none" }}
                  onClick={refreshPreview}
                  disabled={busy}
                >
                  Refresh matches
                </Button>
              </Box>
            )}

            {activeTab === 3 && (
              <UserPagentHistoryTab
                key={targetUsername}
                username={targetUsername}
              />
            )}

            {activeTab === 4 && (
              <Box>
                {isBanned && (
                  <Alert severity="info" sx={{ mb: 2, fontSize: "13px" }}>
                    This user is banned, so their posts were soft-deleted by the
                    ban cascade. Turn on &quot;Include deleted&quot; to review
                    them.
                  </Alert>
                )}
                <UserPostsTab key={targetUsername} username={targetUsername} />
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <ConfirmDialog
        open={confirmOpen}
        title={confirmDialogProps.title}
        message={confirmDialogProps.message}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmKind(null);
        }}
        onConfirm={confirmDialogProps.onConfirm}
        confirmColor="inherit"
        confirmSx={{
          backgroundColor: "#fff",
          color: "#142C5C",
          border: "1px solid #142C5C",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "#f5f5f5",
            boxShadow: "none",
          },
        }}
      />
    </Box>
  );
};

export default UserModification;
